import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import translations from "./translations";

const STORAGE_KEY = "delfi.lang";
const SUPPORTED = ["en", "ar"];

const LanguageContext = createContext({
  lang: "en",
  dir: "ltr",
  t: translations.en,
  setLang: () => {},
  toggle: () => {},
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(stored) ? stored : "en";
  });

  const dir = lang === "ar" ? "rtl" : "ltr";

  // sync to <html lang> + <html dir>
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    if (lang === "ar") {
      document.documentElement.classList.add("lang-ar");
    } else {
      document.documentElement.classList.remove("lang-ar");
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (_) {
      /* ignore quota errors */
    }
  }, [lang, dir]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const toggle = useCallback(() => {
    setLangState((prev) => (prev === "en" ? "ar" : "en"));
  }, []);

  const value = useMemo(
    () => ({
      lang,
      dir,
      t: translations[lang] || translations.en,
      setLang,
      toggle,
    }),
    [lang, dir, setLang, toggle]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

// shorthand — returns the translations bundle for the current language
export function useT() {
  return useContext(LanguageContext).t;
}
